import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import { COHORTS_PATH } from '@verantos-dev/core-constants';
import type { CohortCRUDResponse, RenameCohortData } from '@verantos-dev/types-cohorts';

import { cohortApiSlice } from '../cohortAPISlice';
import { COHORTS_BASE_PATH } from '../constants';
import { authFetch } from './fetchers';
import { invalidateCohort } from './invalidateCohort';
import { COHORT_WRITE_MUTATION_KEY } from './mutationKeys';

/**
 * Canonical TanStack mutation for renaming a cohort profile
 * (PATCH /cohort-profile/:cohortId/rename). The legacy RTK `useRenameCohortMutation`
 * remains available for non-migrated callers; gen-3 rename flows use this hook.
 *
 * Tagged with `COHORT_WRITE_MUTATION_KEY` so `useProjectPageCohortSync`
 * invalidates the project artifact manifest + activity feed on success (OR-6111).
 */
export const useRenameCohortProfileMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation<CohortCRUDResponse, Error, RenameCohortData>({
    mutationKey: [COHORT_WRITE_MUTATION_KEY],
    mutationFn: ({ projectId, cohortId, data }) =>
      authFetch<CohortCRUDResponse>(
        `/${COHORTS_PATH}/${projectId}/${COHORTS_BASE_PATH}/${cohortId}/rename`,
        { method: 'PATCH', body: JSON.stringify(data) }
      ),
    onSuccess: (_, { cohortId, projectId }) => {
      void invalidateCohort(queryClient, { cohortId, projectId });
      // Keep legacy RTK caches in sync. Mirrors the RTK `renameCohort` invalidatesTags.
      // TODO: cleanup with research_assistant_gen_3 ff
      dispatch(
        cohortApiSlice.util.invalidateTags([
          { type: 'cohorts', id: cohortId },
          'cohorts',
          'projects',
        ])
      );
    },
  });
};
